import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Card, CardBody, CardHeader } from "../components/Card";
import { SlotsEditor, type SlotDraft } from "../components/SlotsEditor";
import { api, ApiError } from "../lib/api";
import type { IntentSummary } from "../lib/me";

interface ExampleDraft {
  subject: string;
  snippet: string;
}

const EMPTY_EXAMPLE: ExampleDraft = { subject: "", snippet: "" };

export default function NewCategory() {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [replyMode, setReplyMode] = useState<"direct" | "draft">("draft");
  const [priority, setPriority] = useState(50);
  const [examples, setExamples] = useState<ExampleDraft[]>([
    { ...EMPTY_EXAMPLE },
    { ...EMPTY_EXAMPLE },
    { ...EMPTY_EXAMPLE },
  ]);
  const [slots, setSlots] = useState<SlotDraft[]>([]);
  const [knowledge, setKnowledge] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateExample = (i: number, patch: Partial<ExampleDraft>) =>
    setExamples((arr) => arr.map((ex, idx) => (idx === i ? { ...ex, ...patch } : ex)));

  const addExample = () => setExamples((arr) => [...arr, { ...EMPTY_EXAMPLE }]);
  const removeExample = (i: number) =>
    setExamples((arr) => (arr.length <= 3 ? arr : arr.filter((_, idx) => idx !== i)));

  const validExamples = examples.filter((e) => e.subject.trim() && e.snippet.trim());
  const canSubmit =
    displayName.trim().length > 0 && description.trim().length > 0 && validExamples.length >= 3;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const intent = await api.post<IntentSummary>("/api/intents", {
        displayName: displayName.trim(),
        description: description.trim(),
        examples: validExamples,
        priority,
        replyMode,
        slots: slots.length > 0 ? slots : undefined,
        knowledgeContent: knowledge || undefined,
      });
      navigate(`/categories/${intent.intentId}`, { replace: true });
    } catch (err) {
      const e = err as ApiError;
      setError(e.message ?? "Couldn't create the category.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <Link to="/categories" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Categories
      </Link>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">New category</h1>
        <p className="mt-1 text-sm text-gray-500">
          Define what kind of email this category is for. The AI uses your description and examples
          to recognize matching emails.
        </p>
      </div>

      <Card>
        <CardHeader title="Identity" />
        <CardBody className="space-y-4">
          <Input
            label="Display name"
            placeholder="e.g. Refund request"
            required
            maxLength={60}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-gray-900">Description</label>
            <textarea
              required
              maxLength={500}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is the customer trying to accomplish? What makes this distinct from other categories?"
              className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            <p className="mt-1 text-xs text-gray-500">{description.length}/500</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900">Reply mode</label>
              <select
                value={replyMode}
                onChange={(e) => setReplyMode(e.target.value as "direct" | "draft")}
                className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                <option value="draft">Draft (admin reviews before send)</option>
                <option value="direct">Direct (AI sends autonomously)</option>
              </select>
            </div>
            <Input
              label="Priority"
              type="number"
              min={1}
              max={999}
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) || 50)}
              hint="Lower = evaluated first by deterministic rules."
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Required information (slots)"
          description="Optional. Specify what info this category needs from the customer (order ID, account email, etc.). The AI extracts what it can from the email and asks the customer for anything missing."
        />
        <CardBody>
          <SlotsEditor slots={slots} onChange={setSlots} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Knowledge / Reference material (optional)"
          description="FAQ-style facts the AI uses authoritatively when drafting replies. Keep concise — bullet points work best. 5000 char limit."
        />
        <CardBody className="space-y-2">
          <textarea
            rows={6}
            maxLength={5000}
            value={knowledge}
            onChange={(e) => setKnowledge(e.target.value)}
            placeholder="• Refund policy: full refund within 30 days, partial after.&#10;• Plan limits: Starter = 1k emails/mo, Growth = 10k.&#10;• Reset link expires after 1 hour."
            className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
          <p className="text-right text-xs text-gray-500">{knowledge.length.toLocaleString()}/5,000</p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={`Examples (${validExamples.length}/${examples.length})`}
          description="Few-shot guidance for the AI. Include obvious cases plus edge cases customers actually phrase."
          actions={
            <Button type="button" size="sm" variant="secondary" onClick={addExample}>
              <Plus className="h-3.5 w-3.5" /> Add
            </Button>
          }
        />
        <CardBody className="space-y-4">
          {examples.map((ex, i) => (
            <div key={i} className="space-y-2 rounded-md border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Example {i + 1}
                </span>
                {examples.length > 3 && (
                  <button
                    type="button"
                    onClick={() => removeExample(i)}
                    className="text-gray-400 hover:text-rose-600"
                    aria-label="Remove example"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Input
                label="Subject"
                placeholder="e.g. cancel and refund"
                value={ex.subject}
                onChange={(e) => updateExample(i, { subject: e.target.value })}
              />
              <div>
                <label className="block text-sm font-medium text-gray-900">Snippet</label>
                <textarea
                  rows={2}
                  value={ex.snippet}
                  onChange={(e) => updateExample(i, { snippet: e.target.value })}
                  placeholder="A representative line or two from the body of an email like this."
                  className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                />
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500">Minimum 3 examples. 5–8 is the sweet spot.</p>
        </CardBody>
      </Card>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Link to="/categories">
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </Link>
        <Button type="submit" loading={submitting} disabled={!canSubmit}>
          Create category
        </Button>
      </div>
    </form>
  );
}
